import { useState } from "react";
import { useFormik } from "formik";
import { TextField } from "@mui/material";
import * as yup from "yup";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { HashLoader } from "react-spinners";

import { COLORS } from "../../utils/colors";
import { MainButton } from "../../components/MainButton";
import { checkRegisterCode } from "../../api/registerCodes";

const validationSchema = yup.object({
  email: yup
    .string("Enter your email")
    .email("Enter a valid email")
    .required("Email is required"),
  password: yup
    .string("Enter your password")
    .min(8, "Password should be of minimum 8 characters length")
    .required("Password is required"),
  passwordConfirmation: yup
    .string()
    .oneOf([yup.ref("password"), null], "Passwords must match"),
  code: yup.string("Enter the code").required("Confirmation code is required"),
});

function Register({ handleScreenChange, supabaseClient }) {
  const [loading, setLoading] = useState(false);
  const [errMessage, setErrMessage] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] =
    useState(false);

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = () => setShowPassword(!showPassword);
  const handleClickShowPasswordConf = () =>
    setShowPasswordConfirmation(!showPasswordConfirmation);
  const handleMouseDownPasswordConf = () =>
    setShowPasswordConfirmation(!showPasswordConfirmation);

  const handleFormSubmit = async (values) => {
    try {
      setErrMessage("");
      setLoading(true);
      setEmailSent(false);

      const checkCode = await checkRegisterCode(values.code);

      if (checkCode.error) {
        setErrMessage(checkCode.error.message);
        setLoading(false);
        return;
      }

      const { data, error } = await supabaseClient.auth.signUp({
        email: values.email,
        password: values.password,
      });

      setLoading(false);
      setEmailSent(true);

      if (error) {
        console.log("error: ", error);
        setErrMessage(`${error}`);
      }
    } catch (error) {
      setEmailSent(false);
      setLoading(false);
      setErrMessage(`${error}`);
      console.log("error: ", error);
    }
  };

  // create formik object to handle the form
  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      passwordConfirmation: "",
      code: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => handleFormSubmit(values),
  });

  return (
    <div className="auth-container">
      {/* <Logo /> */}
      <p
        className="auth-header"
        style={{ marginBottom: "0px", fontWeight: "800" }}
      >
        Create an OOVAL account
      </p>
      <p
        id="subheader"
        style={{ margin: "10px 0px 30px", alignSelf: "center" }}
      >
        Please have the validation code ready
      </p>
      <br />
      <div className="register-form-wrapper">
        <form className="register-form" onSubmit={formik.handleSubmit}>
          <p id="subheaderBlack" style={{ marginBottom: "3px" }}>
            Your email address
          </p>
          <TextField
            fullWidth
            size="small"
            className="input"
            name="email"
            placeholder="richard.hendriks@hooli.com"
            value={formik.values.email}
            onChange={formik.handleChange}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            sx={{
              "& label": { paddingLeft: (theme) => theme.spacing(1) },
              "& fieldset": {
                borderRadius: "6px",
                border: `2px solid #f0f1f5`,
                transition: ".2s ease-in-out all",
              },
            }}
          />
          <p id="subheaderBlack" style={{ marginBottom: "3px" }}>
            Your password
          </p>
          <TextField
            fullWidth
            size="small"
            className="input"
            name="password"
            placeholder="****************"
            type={showPassword ? "text" : "password"}
            value={formik.values.password}
            onChange={formik.handleChange}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
            sx={{
              "& label": { paddingLeft: (theme) => theme.spacing(1) },
              "& fieldset": {
                borderRadius: "6px",
                border: `2px solid #f0f1f5`,
                transition: ".2s ease-in-out all",
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    // aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                  >
                    {showPassword ? (
                      <AiFillEyeInvisible size={20} color={COLORS.lightGrey} />
                    ) : (
                      <AiFillEye size={20} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <p id="subheaderBlack" style={{ marginBottom: "3px" }}>
            Repeat password
          </p>
          <TextField
            fullWidth
            size="small"
            className="input"
            name="passwordConfirmation"
            placeholder="****************"
            type={showPasswordConfirmation ? "text" : "password"}
            value={formik.values.passwordConfirmation}
            onChange={formik.handleChange}
            error={
              formik.touched.passwordConfirmation &&
              Boolean(formik.errors.passwordConfirmation)
            }
            helperText={
              formik.touched.passwordConfirmation &&
              formik.errors.passwordConfirmation
            }
            sx={{
              "& label": { paddingLeft: (theme) => theme.spacing(1) },
              "& fieldset": {
                borderRadius: "6px",
                border: `2px solid #f0f1f5`,
                transition: ".2s ease-in-out all",
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    // aria-label="toggle password visibility"
                    onClick={handleClickShowPasswordConf}
                    onMouseDown={handleMouseDownPasswordConf}
                  >
                    {showPasswordConfirmation ? (
                      <AiFillEyeInvisible size={20} color={COLORS.lightGrey} />
                    ) : (
                      <AiFillEye size={20} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <p id="subheaderBlack" style={{ marginBottom: "3px" }}>
            Validation code
          </p>
          <TextField
            fullWidth
            size="small"
            className="input"
            name="code"
            placeholder="secret code"
            type="text"
            value={formik.values.code}
            onChange={formik.handleChange}
            error={formik.touched.code && Boolean(formik.errors.code)}
            helperText={formik.touched.code && formik.errors.code}
            sx={{
              "& label": { paddingLeft: (theme) => theme.spacing(1) },
              "& fieldset": {
                borderRadius: "6px",
                border: `2px solid #f0f1f5`,
                transition: ".2s ease-in-out all",
              },
            }}
          />
          <div style={{ height: "9px" }} />
          {errMessage && (
            <p className="error-message" style={{ marginBottom: "5px" }}>
              {errMessage}
            </p>
          )}
          <MainButton
            type="submit"
            disabled={loading || emailSent}
            text={
              loading ? (
                <>
                  <HashLoader size={20} color={COLORS.grey} /> Create account
                </>
              ) : (
                "Create account"
              )
            }
            color={COLORS.black}
            textColor={COLORS.white}
            textHoverColor={COLORS.white}
            hoverColor={COLORS.mainAccent}
            shadowColor={"transparent"}
            onClickAction={() => formik.handleSubmit()}
          />
          {emailSent && !loading ? (
            <p style={{ textAlign: "center", marginTop: "6px" }}>
              Please verify your email address
            </p>
          ) : null}
          <br />
          <br />
          <p className="bottom-link">
            <span onClick={() => handleScreenChange("login")}>
              I already have an account
            </span>
          </p>
        </form>
      </div>
    </div>
  );
}

export default Register;
